import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function exportPurchaseReturnsToPDF(
  purchaseReturns,
  filename = "purchase_returns.pdf"
) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let currentY = 10;

  doc.setProperties({
    title: "Purchase Returns",
    subject: "Purchase Returns Data Export",
    creator: "Afghan Cosmos Group",
  });

  // Helper function to get first currency value
  const firstCurrencyValue = (obj) => {
    if (obj && typeof obj === "object") {
      const firstEntry = Object.values(obj)[0];
      if (firstEntry && typeof firstEntry === "object")
        return firstEntry.source ?? "-";
      return firstEntry ?? "-";
    }
    return obj ?? "-";
  };

  const addHeader = () => {
    const shopName = "Afghan Cosmos ";
 
    try {
      const logoUrl = `${process.env.REACT_APP_IMAGE_URL}images/logo.jpeg`;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = logoUrl;
      doc.addImage(img, "JPEG", pageWidth - 25, 2, 10, 10);
    } catch (error) {
      console.error("Failed to add logo:", error);
    }

    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 102, 204);
    doc.text("Purchase Returns Report", pageWidth / 2, 9, { align: "center" });

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(80, 80, 80);
    doc.text(shopName, 10, 7);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 10, 11);

    return 18;
  };

  currentY = addHeader();

  // Calculate summary totals
  const summaryTotals = purchaseReturns.reduce((acc, ret) => {
    const totalObj = ret.purchase_details?.total_amount || {};
    const currency = Object.keys(totalObj)[0] || "";
    const purchaseTotal = ret.purchase_details?.total_amount ? 
      parseFloat(firstCurrencyValue(ret.purchase_details.total_amount)) : 
      (ret.purchase_details?.purchase_items?.reduce((sum, item) => sum + (item.buy_price * item.quantity), 0) || 0);
    
    const refundTotal = parseFloat(ret.total_refound_amount) || 
      ret.return_items?.reduce((sum, item) => sum + parseFloat(item.refund_amount || 0), 0) || 0;
    
    return {
      totalPurchaseAmount: acc.totalPurchaseAmount + purchaseTotal,
      totalRefundAmount: acc.totalRefundAmount + refundTotal,
      currency: currency || acc.currency
    };
  }, { totalPurchaseAmount: 0, totalRefundAmount: 0, currency: "" });

  purchaseReturns.forEach((ret) => {
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = addHeader();
    }

    const totalObj = ret.purchase_details?.total_amount || {};
    const currency = Object.keys(totalObj)[0] || "-";
    const purchaseTotal = ret.purchase_details?.total_amount ? 
      `${firstCurrencyValue(ret.purchase_details.total_amount)} ${currency}` : 
      (ret.purchase_details?.purchase_items?.reduce((sum, item) => sum + (item.buy_price * item.quantity), 0) || 0).toFixed(2);

    autoTable(doc, {
      head: [
        [
          "ID",
          "Purchase Reference",
          "Return Date",
          "Reason",
          "Purchase Total",
          "Refund Total",
          "Item Count",
        ],
      ],
      body: [
        [
          ret.id,
          ret.purchase_details?.id ? `Purchase #${ret.purchase_details.id}` : "-",
          ret.return_date?.slice(0, 10) || "-",
          ret.reason || "-",
          purchaseTotal,
          `${ret.total_refound_amount || ret.return_items?.reduce((sum, item) => sum + parseFloat(item.refund_amount || 0), 0).toFixed(2) || "0.00"} ${currency}`,
          ret.return_items?.length || 0,
        ],
      ],
      startY: currentY,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fontSize: 8,
        fontStyle: "bold",
        fillColor: [51, 102, 204],
        textColor: [255, 255, 255],
        halign: "center",
        cellPadding: 2,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      margin: { top: 1, bottom: 1, left: 3, right: 3 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) currentY = addHeader();
      },
    });

    currentY = doc.lastAutoTable.finalY + 0;

    if (ret.return_items?.length) {
      autoTable(doc, {
        head: [["Product", "Return Qty", "Buy Price", "Refund Amount"]],
        body: ret.return_items.map((item) => {
          const buyPrice = firstCurrencyValue(item.purchase_item_details?.buy_price) || "0";
          return [
            item.purchase_item_details?.product_details?.name || "-",
            item.return_quantity,
            buyPrice,
            item.refund_amount,
          ];
        }),
        startY: currentY,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          halign: "center",
          valign: "middle",
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
          fillColor: [220, 230, 242],
          textColor: [50, 50, 50],
          halign: "center",
          cellPadding: 2,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        margin: { top: 1, bottom: 1, left: 3, right: 3 },
      });

      currentY = doc.lastAutoTable.finalY + 2;
    }
  });

  // Add summary table
  if (currentY > pageHeight - 40) {
    doc.addPage();
    currentY = addHeader();
  }

  autoTable(doc, {
    head: [["Summary", "Total Purchase Amount", "Total Refund Amount"]],
    body: [[
      "TOTALS",
      `${summaryTotals.totalPurchaseAmount.toFixed(2)} ${summaryTotals.currency}`,
      `${summaryTotals.totalRefundAmount.toFixed(2)} ${summaryTotals.currency}`
    ]],
    startY: currentY + 5,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      fontStyle: "bold",
    },
    headStyles: {
      fontSize: 10,
      fontStyle: "bold",
      fillColor: [51, 102, 204],
      textColor: [255, 255, 255],
    },
    bodyStyles: {
      fillColor: [245, 245, 245],
      fontStyle: "bold",
    },
    margin: { left: 3, right: 3 },
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(0, pageHeight - 11, pageWidth, pageHeight - 11);

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("تجزوی هول سیل", pageWidth / 2, pageHeight - 5, {
      align: "center",
    });

    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, pageHeight - 5, {
      align: "right",
    });
  }

  doc.save(filename);
}